import type { MutableRefObject, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useEditContext, useUpdate } from "ra-core";
import type { ZoomMeetingSummary } from "../types";

export type MeetingPatch = Partial<ZoomMeetingSummary>;

/**
 * A section of the page that can hold unsaved work. `save` is passed as a ref so
 * the provider always calls the current closure without the section having to
 * re-register on every render.
 */
type Section = {
  label: string;
  dirty: boolean;
  save: MutableRefObject<() => Promise<boolean>>;
};

export type MeetingSave = {
  /** Queue a partial patch. Rejects if the write fails. */
  save: (patch: MeetingPatch) => Promise<void>;
  /** Send anything still queued and wait for the queue to drain. */
  flush: () => Promise<unknown>;
  /** False while the record is not safe to build a write from. */
  canSave: boolean;
  /** Labels of the sections holding unsaved work, for the leave prompt. */
  dirtyLabels: string[];
  /** Save every dirty section. False if any of them failed. */
  saveDirty: () => Promise<boolean>;
};

/**
 * Report a section's unsaved state to the page. Call from a section component.
 *
 * The effect is keyed on `dirty` alone — registering during render would set
 * state in a parent while a child renders, and re-registering on every render
 * would re-render the whole page subtree per keystroke.
 */
export function useReportDirty(
  key: string,
  label: string,
  dirty: boolean,
  save: () => Promise<boolean>,
) {
  const { report, forget } = useContext(MeetingSectionsContext) ?? {};
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    report?.(key, { label, dirty, save: saveRef });
    return () => forget?.(key);
  }, [key, label, dirty, report, forget]);
}

const MeetingSectionsContext = createContext<{
  report: (key: string, section: Section) => void;
  forget: (key: string) => void;
} | null>(null);

/**
 * A meeting record read from the list is missing columns.
 *
 * `useGetList` seeds the getOne cache with every list row, and the list reads
 * `zoom_meetings_summary`, which has no `custom_summaries` column. So for one
 * round-trip after arriving from the list, the form holds a view-shaped record
 * whose `custom_summaries` is `undefined`. A patch built from that and PATCHed
 * onto a jsonb column replaces rather than merges, destroying every summary
 * already stored on the meeting.
 *
 * The column is `not null` in the base table, so `undefined` means exactly one
 * thing: this record came from the view and the real one has not arrived yet.
 *
 * `isPlaceholderData` looks like the natural check and is useless here — ra-core
 * never sets `placeholderData` on these queries (`useGetOne`, `useGetList` and
 * `useEditController` contain no reference to it), and a seeded row is written
 * as ordinary cached data, so the flag is false through the entire window.
 */
const isViewShaped = (record: ZoomMeetingSummary | undefined) =>
  record == null || record.custom_summaries === undefined;

const MeetingSaveContext = createContext<MeetingSave | null>(null);

/**
 * The single writer for the meeting record behind the review page.
 *
 * Mounted once per page, deliberately. A hook instantiated per component would
 * give every task row its own queue, and two rows confirmed in quick succession
 * would race two full-`tasks`-array PATCHes on one row, last write winning and
 * dropping the other's `task_id`. One provider, one queue, one writer.
 *
 * Two rules:
 *
 * 1. **Serialize and coalesce.** TanStack mutations do not queue, so a task
 *    confirm can overlap an autosaved summary. Every write chains onto the
 *    previous one, and patches that pile up while a write is in flight are
 *    merged, so a burst of edits costs one request carrying the latest state
 *    rather than one request per edit replaying superseded values.
 *
 * 2. **Never write from a view-shaped record.** See `isViewShaped` above.
 *
 * What is NOT a reason: cache invalidation. `useUpdate` does not invalidate in
 * ra-core 5.14 — its own `updateCache` reconciles via `setQueryData`, which is
 * what this page wants, so there is nothing to hand-roll.
 */
export const MeetingSaveProvider = ({ children }: { children: ReactNode }) => {
  const [update] = useUpdate();
  const { record } = useEditContext<ZoomMeetingSummary>();

  // Read inside queued callbacks, which run long after the render that set them.
  const recordRef = useRef(record);
  recordRef.current = record;

  // The queue tail. Deliberately never rejects: one failed write must not
  // poison every write behind it.
  const chainRef = useRef<Promise<unknown>>(Promise.resolve());
  // Merged but not yet sent.
  const pendingRef = useRef<MeetingPatch | null>(null);

  const save = useCallback(
    (patch: MeetingPatch): Promise<void> => {
      pendingRef.current = { ...(pendingRef.current ?? {}), ...patch };

      const link = chainRef.current.then(async () => {
        const current = recordRef.current;
        const id = current?.id;
        if (id == null) {
          throw new Error("useSaveMeeting: no meeting record to write to");
        }
        if (isViewShaped(current)) {
          throw new Error(
            "useSaveMeeting: refusing to write before the full record has loaded",
          );
        }

        const data = pendingRef.current;
        pendingRef.current = null;
        // Either an earlier link already sent these, or this is a flush with
        // nothing queued behind it.
        if (data == null || Object.keys(data).length === 0) return;

        try {
          await update(
            "zoom_meetings",
            { id, data, previousData: { id } },
            { returnPromise: true },
          );
        } catch (error) {
          // Put the patch back so it is not silently dropped. Anything queued
          // since is newer and wins.
          pendingRef.current = { ...data, ...(pendingRef.current ?? {}) };
          throw error;
        }
      });

      chainRef.current = link.catch(() => undefined);
      return link;
    },
    [update],
  );

  // Queues an empty patch, which sends whatever is parked and no-ops when
  // nothing is. Awaiting the chain alone would not do: a write that failed
  // restores its patch with no link scheduled to carry it.
  const flush = useCallback(() => save({}).catch(() => undefined), [save]);

  // Always current, so `saveDirty` never calls a stale closure.
  const sectionsRef = useRef(new Map<string, Section>());
  // Only the labels live in state, because only they drive a render (the prompt
  // and the beforeunload handler).
  const [dirtyLabels, setDirtyLabels] = useState<string[]>([]);

  const syncDirtyLabels = useCallback(() => {
    const next = [...sectionsRef.current.values()]
      .filter((s) => s.dirty)
      .map((s) => s.label);
    // Returning `prev` unchanged is what stops a re-render on every report.
    setDirtyLabels((prev) =>
      prev.length === next.length && prev.every((l, i) => l === next[i])
        ? prev
        : next,
    );
  }, []);

  const report = useCallback(
    (key: string, section: Section) => {
      sectionsRef.current.set(key, section);
      syncDirtyLabels();
    },
    [syncDirtyLabels],
  );

  const forget = useCallback(
    (key: string) => {
      sectionsRef.current.delete(key);
      syncDirtyLabels();
    },
    [syncDirtyLabels],
  );

  const saveDirty = useCallback(async () => {
    const dirty = [...sectionsRef.current.values()].filter((s) => s.dirty);
    for (const section of dirty) {
      if (!(await section.save.current())) return false;
    }
    return true;
  }, []);

  return (
    <MeetingSectionsContext.Provider value={{ report, forget }}>
      <MeetingSaveContext.Provider
        value={{
          save,
          flush,
          canSave: !isViewShaped(record),
          dirtyLabels,
          saveDirty,
        }}
      >
        {children}
      </MeetingSaveContext.Provider>
    </MeetingSectionsContext.Provider>
  );
};

export function useSaveMeeting(): MeetingSave {
  const context = useContext(MeetingSaveContext);
  if (context == null) {
    throw new Error("useSaveMeeting must be used inside <MeetingSaveProvider>");
  }
  return context;
}
